import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { backoffice } from "@/endpoints/dryp/backoffice";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { FilterableCheckboxGroupDropdown, type CheckboxGroup } from "@/components/FilterableCheckboxGroupDropdown";
import { Building2, ChevronDown } from "lucide-react";

interface OrganizationsFilterProps {
	selected: string[];
	onChange: (values: string[]) => void;
}

export const OrganizationsFilter = observer(({ selected, onChange }: OrganizationsFilterProps) => {
	const organizationsApi = useAsyncStateStore(backoffice.getOrganizations);

	useEffect(() => {
		organizationsApi.fetch({ pageNumber: 0, pageSize: 100000 }, { sort: "name", direction: "asc" });
	}, []);

	const organizations = organizationsApi.data?.items || [];

	const groups: CheckboxGroup[] = useMemo(
		() => [
			{
				name: "Organizations",
				items: organizations.map(organization => ({
					id: organization.id,
					name: organization.name,
					checked: selected.includes(organization.id),
				})),
			},
		],
		[organizations, selected],
	);

	return (
		<FilterableCheckboxGroupDropdown
			groups={groups}
			onCheckedChange={(id, checked) => {
				const next = checked
					? Array.from(new Set([...selected, id]))
					: selected.filter(organizationId => organizationId !== id);
				onChange(next);
			}}
			onToggleAll={checked => {
				onChange(checked ? organizations.map(organization => organization.id) : []);
			}}
			loading={organizationsApi.loading}
			buttonVariant="outline"
			buttonLabel="Organizations"
			buttonClassName="flex items-center gap-2"
			renderItem={item => (
				<div className="flex items-center gap-2">
					<Building2 className="h-4 w-4 text-blue-400" />
					{item.name}
				</div>
			)}
			buttonContent={
				<span className="flex items-center gap-2">
					{selected.length > 0 ? (
						<span className="text-[11px] text-muted-foreground">({selected.length} selected)</span>
					) : (
						<span>All</span>
					)}
					<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
				</span>
			}
			contentAlign="end"
			searchPlaceholder="Search organizations..."
		/>
	);
});
